import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Select from "components/shared/inputs/Select";

export const VIEWS = ["windows", "shifts"] as const;
export type SelfSchedulingView = typeof VIEWS[number];
const DEFAULT_VIEW: SelfSchedulingView = "windows";

export const useSelfSchedulingView = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const view = useMemo<SelfSchedulingView>(() => {
        const value = searchParams.get("view");
        return VIEWS.includes(value as SelfSchedulingView) ? (value as SelfSchedulingView) : DEFAULT_VIEW;
    }, [searchParams]);

    const setView = useCallback((value: SelfSchedulingView) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("view", value);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    return [view, setView] as const;
};

function ViewSelect() {
    const { t } = useTranslation();
    const [view, setView] = useSelfSchedulingView();

    return (
        <Select
            className="tw-w-52 tw-shadow-none"
            showSearch={false}
            value={view}
            onChange={(value) => setView(value as SelfSchedulingView)}
        >
            {VIEWS.map((option) => (
                <Select.Item key={option} value={option}>
                    {option === "windows" ? t("self-scheduling-window") : t("unassigned-shifts")}
                </Select.Item>
            ))}
        </Select>
    );
}

export default ViewSelect;
